import * as React from "react";
import { cn } from "@finster/ui";
import { Check, Loader2, Square } from "lucide-react";
import { SimpleMarkdown } from "../shared/SimpleMarkdown";
import { useStickToBottom } from "../../hooks/useStickToBottom";
import { CreationProgress } from "./DeckCreator";
import type { HopView, ToolView } from "../../services/ai/agentStream";

interface DeckCreationProgressProps {
  progress: CreationProgress;
  onCancel?: () => void;
}

const NARRATOR_MARKDOWN_CLASS = cn(
  "[&_h1]:!text-[13.5px]",
  "[&_h2]:!text-[13.5px]",
  "[&_h3]:!text-[13.5px]",
  "[&_h4]:!text-[13.5px]",
  "[&_div]:!text-[12.5px]",
  "[&_li]:!text-[12.5px]",
  "[&_ol]:!text-[12.5px]",
  "[&_ul]:!text-[12.5px]",
  "[&_h1]:!pt-0",
  "[&_h2]:!pt-0",
  "[&_h3]:!pt-0",
  "[&_h4]:!pt-0",
  "[&>*:last-child]:!mb-0"
);

function isNarratorSectionHeading(line: string | undefined): boolean {
  return Boolean(line?.match(/^\*\*[^*\n][\s\S]*\*\*$/));
}

function isMarkdownBlockStarter(line: string | undefined): boolean {
  return Boolean(line?.match(/^(\*\*[^*\n][\s\S]*\*\*|[-*+]\s+|\d+\.\s+|#{1,6}\s+|>\s+|\|)/));
}

export function normalizeNarratorMarkdownForActivity(text: string): string {
  const lines = text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.trimEnd());
  const output: string[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim();
    if (!line) {
      const previous = output[output.length - 1]?.trim();
      const next = lines.slice(index + 1).find((candidate) => candidate.trim().length > 0)?.trim();
      const shouldKeepBlankLine =
        isNarratorSectionHeading(previous) ||
        isMarkdownBlockStarter(next) ||
        isMarkdownBlockStarter(previous);

      if (shouldKeepBlankLine && output[output.length - 1] !== "") {
        output.push("");
      }
      continue;
    }

    output.push(line);
  }

  return output.join("\n").trim();
}

type EditActivity = {
  id: string;
  title: string;
  body: string;
  steps?: string[];
  stepStatuses?: Array<"pending" | "active" | "done" | "error">;
  currentStepIndex?: number;
  active?: boolean;
  complete?: boolean;
  /** Completion summary appended below the step list in the same row. */
  footer?: string;
};

type ActivityTimelineEntry =
  | { id: string; type: "thinking"; text: string; attachedEdits?: EditActivity[] }
  | { id: string; type: "edit_step"; edit: EditActivity };

function splitNarratorTextIntoTimelineSections(text: string): string[] {
  const lines = text.split("\n");
  const sections: string[] = [];
  let currentSection: string[] = [];

  for (const line of lines) {
    if (isNarratorSectionHeading(line.trim()) && currentSection.some((sectionLine) => sectionLine.trim())) {
      sections.push(currentSection.join("\n").trim());
      currentSection = [];
    }

    currentSection.push(line);
  }

  if (currentSection.some((line) => line.trim())) {
    sections.push(currentSection.join("\n").trim());
  }

  return sections;
}

function getNarratorSectionHeading(text: string): string | null {
  const firstLine = text.split("\n").find((line) => line.trim())?.trim();
  if (!isNarratorSectionHeading(firstLine)) return null;
  return firstLine.replace(/^\*\*/, "").replace(/\*\*$/, "").trim().toLowerCase();
}

function getNarratorSectionBody(text: string): string {
  const lines = text.split("\n");
  const headingLineIndex = lines.findIndex((line) => line.trim().length > 0);
  if (headingLineIndex === -1) return "";
  if (!isNarratorSectionHeading(lines[headingLineIndex].trim())) return text.trim();
  return lines.slice(headingLineIndex + 1).join("\n").trim();
}

function getNarratorBodyDedupeKey(text: string): string {
  return getNarratorSectionBody(text)
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function hasDuplicateNarratorBody(entries: ActivityTimelineEntry[], section: string): boolean {
  const sectionBodyKey = getNarratorBodyDedupeKey(section);
  if (!sectionBodyKey) return false;

  return entries.some(
    (entry) => entry.type === "thinking" && getNarratorBodyDedupeKey(entry.text) === sectionBodyKey
  );
}

function appendThinkingEntries(
  entries: ActivityTimelineEntry[],
  idPrefix: string,
  text: string
): void {
  const normalizedText = normalizeNarratorMarkdownForActivity(text);
  if (!normalizedText) return;

  splitNarratorTextIntoTimelineSections(normalizedText).forEach((section, sectionIndex) => {
    const previousEntry = entries[entries.length - 1];
    const currentHeading = getNarratorSectionHeading(section);
    const previousHeading =
      previousEntry?.type === "thinking" && !previousEntry.attachedEdits?.length
        ? getNarratorSectionHeading(previousEntry.text)
        : null;

    if (hasDuplicateNarratorBody(entries, section)) {
      return;
    }

    if (currentHeading && previousHeading === currentHeading) {
      const body = getNarratorSectionBody(section);
      if (body) {
        previousEntry.text = normalizeNarratorMarkdownForActivity(`${previousEntry.text}\n\n${body}`);
      }
      return;
    }

    entries.push({ id: `${idPrefix}-${sectionIndex}`, type: "thinking", text: section });
  });
}

function isApplyEditNarrationEntry(text: string): boolean {
  const firstLine = text.split("\n").find((line) => line.trim())?.replace(/\*/g, "").trim() ?? "";
  return /\b(apply|applying)\b.*\b(edits?|updates?)\b/i.test(firstLine);
}

function formatApplyEditProgressStep(message: string): string {
  const trimmed = message.trim();
  const preparingMatch = trimmed.match(/^Preparing edits for (\d+) slides?\.$/i);
  if (preparingMatch) {
    return `Preparing ${preparingMatch[1]} slide update${preparingMatch[1] === "1" ? "" : "s"}`;
  }

  switch (trimmed) {
    case "Preparing slide edits.":
      return "Preparing slide updates";
    case "Checking current slide order before inserting new slides.":
      return "Checking slide order";
    case "Applying edits in PowerPoint.":
      return "Updating slides in PowerPoint";
    case "Reading applied edit results.":
      return "Reading update results";
    case "Resolving newly created slides.":
      return "Resolving inserted slides";
    case "Refreshing slide layout after the edit.":
      return "Refreshing slide layout";
    case "Slide edits applied.":
      return "Finishing slide update";
    default:
      return trimmed.replace(/\.$/, "");
  }
}

function getApplyEditProgressSteps(progressMessages: string[]): string[] {
  const steps = progressMessages.map(formatApplyEditProgressStep).filter(Boolean);
  return Array.from(new Set(steps));
}

type ApplyEditSummary = {
  intentText?: string;
  slideSummaries: string[];
};

const DRAFTING_SLIDE_UPDATES_TEXT = "Drafting Slide Updates...";
const APPLYING_DRAFTED_SLIDES_TEXT = "Applying drafted slides in PowerPoint...";

function extractStreamingJsonStringField(text: string, field: string): string | undefined {
  const match = new RegExp(`"${field}"\\s*:\\s*"`).exec(text);
  if (!match) return undefined;

  let encoded = "";
  let cursor = match.index + match[0].length;
  while (cursor < text.length) {
    const char = text[cursor];
    if (char === "\\") {
      encoded += char + (text[cursor + 1] ?? "");
      cursor += 2;
      continue;
    }
    if (char === '"') break;
    encoded += char;
    cursor += 1;
  }

  if (!encoded.trim()) return undefined;
  try {
    return JSON.parse(`"${encoded}"`) as string;
  } catch {
    return encoded;
  }
}

function getTextBeforeFirstField(text: string, field: string): string {
  const match = new RegExp(`"${field}"\\s*:`).exec(text);
  return match ? text.slice(0, match.index) : text;
}

function getTextAfterArrayFieldStart(text: string, field: string): string | undefined {
  const match = new RegExp(`"${field}"\\s*:\\s*\\[`).exec(text);
  if (!match) return undefined;
  return text.slice(match.index + match[0].length);
}

function splitTopLevelObjectFragments(arrayContent: string): string[] {
  const fragments: string[] = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  let escaped = false;

  for (let index = 0; index < arrayContent.length; index += 1) {
    const char = arrayContent[index];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
      continue;
    }

    if (char === "{") {
      if (depth === 0) start = index;
      depth += 1;
      continue;
    }

    if (char === "}") {
      if (depth > 0) depth -= 1;
      if (depth === 0 && start >= 0) {
        fragments.push(arrayContent.slice(start, index + 1));
        start = -1;
      }
    }
  }

  if (start >= 0) {
    fragments.push(arrayContent.slice(start));
  }

  return fragments;
}

function cleanApplyEditIntentText(text: string | undefined): string | undefined {
  const summaryText = text
    ?.replace(/^summary\s*:\s*/i, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!summaryText || /^[{[]/.test(summaryText)) return undefined;
  return summaryText.length > 260 ? `${summaryText.slice(0, 257).trimEnd()}...` : summaryText;
}

function cleanSlideSummaryText(text: string | undefined): string | undefined {
  const summaryText = text?.replace(/\s+/g, " ").trim();
  if (!summaryText || /^[{[]/.test(summaryText)) return undefined;
  return summaryText.length > 220 ? `${summaryText.slice(0, 217).trimEnd()}...` : summaryText;
}

function extractApplyEditSummaryFromText(
  text: string,
  includeSlideSummaries: boolean
): ApplyEditSummary {
  const withoutFence = text.replace(/^```(?:json)?\s*/i, "").split(/```/)[0]?.trim() ?? "";
  if (!withoutFence) return { slideSummaries: [] };

  const beforeSlides = getTextBeforeFirstField(withoutFence, "slides");
  const intentText = cleanApplyEditIntentText(
    extractStreamingJsonStringField(beforeSlides, "summary")
  );
  const slideSummaries: string[] = [];

  if (includeSlideSummaries) {
    const slidesContent = getTextAfterArrayFieldStart(withoutFence, "slides");
    if (slidesContent) {
      for (const fragment of splitTopLevelObjectFragments(slidesContent)) {
        const beforeShapes = getTextBeforeFirstField(fragment, "shapes");
        const summary = cleanSlideSummaryText(
          extractStreamingJsonStringField(beforeShapes, "summary")
        );
        if (summary && !slideSummaries.includes(summary)) {
          slideSummaries.push(summary);
        }
      }
    }
  }

  return { intentText, slideSummaries };
}

function getApplyEditSummary(hop: HopView, includeSlideSummaries: boolean): ApplyEditSummary {
  const rawText = [hop.applyEditArgsText, hop.outputText].filter(Boolean).join("").trim();
  if (!rawText) return { slideSummaries: [] };

  const streamedSummary = extractApplyEditSummaryFromText(rawText, includeSlideSummaries);
  if (streamedSummary.intentText || streamedSummary.slideSummaries.length > 0) {
    return streamedSummary;
  }

  const withoutFence = rawText.replace(/^```(?:json)?\s*/i, "").split(/```/)[0]?.trim() ?? "";
  if (!hop.tools.some((tool) => tool.tool === "apply_edits") || /^[{[]/.test(withoutFence)) {
    return { slideSummaries: [] };
  }

  const jsonStart = withoutFence.search(/[\n\r]\s*[{[]/);
  const freeformIntent = jsonStart >= 0 ? withoutFence.slice(0, jsonStart) : withoutFence;
  return { intentText: cleanApplyEditIntentText(freeformIntent), slideSummaries: [] };
}

function outputSummaryToApplyEditActivity(
  hop: HopView,
  progress: CreationProgress,
  includeSlideSummaries: boolean
): EditActivity | null {
  if (progress.phase === "polishing") return null;
  if (hop.tools.some((tool) => tool.tool === "apply_edits")) return null;
  const applySummary = getApplyEditSummary(hop, includeSlideSummaries);
  if (!applySummary.intentText && applySummary.slideSummaries.length === 0) return null;

  const isTerminal =
    progress.status === "complete" || progress.status === "error" || progress.status === "cancelled";
  const steps = includeSlideSummaries ? applySummary.slideSummaries : [];
  return {
    id: `apply-summary-${hop.hop}`,
    title: "Apply edits",
    body: includeSlideSummaries
      ? DRAFTING_SLIDE_UPDATES_TEXT
      : applySummary.intentText ?? "Preparing slide updates",
    steps: steps.length > 0 ? steps : undefined,
    currentStepIndex: steps.length > 0 ? steps.length - 1 : undefined,
    active: !isTerminal,
  };
}

function applyEditToolToActivity(
  tool: ToolView,
  applySummary: ApplyEditSummary,
  includeSlideSummaries: boolean
): EditActivity {
  const isCalling = tool.status === "calling";
  const progressMessages = tool.progress ?? [];
  const progressSteps = getApplyEditProgressSteps(progressMessages);
  const data = tool.result?.data as
    | { modelSummary?: string; slideSummaries?: unknown }
    | undefined;
  const modelSummary = data?.modelSummary;
  const resultSlideSummaries = Array.isArray(data?.slideSummaries)
    ? data.slideSummaries.filter((summary): summary is string => typeof summary === "string")
    : [];
  const slideSummarySteps = includeSlideSummaries
    ? (resultSlideSummaries.length > 0 ? resultSlideSummaries : applySummary.slideSummaries)
    : [];
  const steps = [...slideSummarySteps, ...progressSteps];
  const isApplyingInPowerPoint = progressMessages.some(
    (message) => message.trim() === "Applying edits in PowerPoint."
  );
  const currentStepIndex =
    progressSteps.length > 0
      ? steps.length - 1
      : slideSummarySteps.length > 0
        ? slideSummarySteps.length - 1
        : -1;
  const body = includeSlideSummaries
    ? isApplyingInPowerPoint
      ? APPLYING_DRAFTED_SLIDES_TEXT
      : DRAFTING_SLIDE_UPDATES_TEXT
    : applySummary.intentText ??
      (currentStepIndex >= 0 ? steps[currentStepIndex] : "Starting slide update");

  if (isCalling) {
    return {
      id: `apply-${tool.callId}`,
      active: true,
      title: "Apply edits",
      body,
      steps: steps.length > 0 ? steps : undefined,
      currentStepIndex,
    };
  }

  const completionSummary = modelSummary || "The slide edits are applied.";
  return {
    id: `apply-${tool.callId}`,
    title: "Apply edits",
    body,
    steps: steps.length > 0 ? steps : undefined,
    currentStepIndex: steps.length > 0 ? steps.length - 1 : undefined,
    complete: steps.length > 0,
    footer: completionSummary,
  };
}

type PolishSlideStatus = "pending" | "active" | "done" | "error";

function getPolishSlideStatus(
  polish: NonNullable<CreationProgress["polish"]>,
  position: number
): PolishSlideStatus {
  const explicitStatus = polish.slideStatuses?.[position];
  if (explicitStatus) return explicitStatus;
  if (position <= polish.current) return "done";
  if (position === polish.current + 1 && polish.current < polish.total) return "active";
  return "pending";
}

function formatPolishProgressStep(
  position: number,
  status: PolishSlideStatus,
  title: string | undefined
): string {
  const slideLabel = title ? `Slide ${position}: ${title}` : `Slide ${position}`;
  switch (status) {
    case "done":
      return `${slideLabel} polished`;
    case "active":
      return `${slideLabel} polishing`;
    case "error":
      return `${slideLabel} needs review`;
    case "pending":
    default:
      return `${slideLabel} queued`;
  }
}

function getPolishActivity(progress: CreationProgress): EditActivity | null {
  const polish = progress.polish;
  if (!polish || polish.total <= 0) return null;

  const slideStatuses = Array.from({ length: polish.total }, (_, index) => {
    const position = index + 1;
    return {
      position,
      status: getPolishSlideStatus(polish, position),
      title: polish.slideTitles?.[position] ?? progress.completedSlides[position - 1],
    };
  });
  const activeIndex = slideStatuses.findIndex((slide) => slide.status === "active");
  const nextPendingIndex = slideStatuses.findIndex((slide) => slide.status === "pending");
  const lastCompletedIndex = slideStatuses.reduce(
    (latest, slide, index) => (slide.status === "done" ? index : latest),
    -1
  );
  const currentStepIndex =
    activeIndex >= 0
      ? activeIndex
      : polish.current < polish.total && nextPendingIndex >= 0
        ? nextPendingIndex
        : Math.max(lastCompletedIndex, 0);
  const errorCount = slideStatuses.filter((slide) => slide.status === "error").length;
  const isTerminal =
    progress.status === "complete" || progress.status === "cancelled" || progress.status === "error";
  const active =
    !isTerminal &&
    (polish.current < polish.total || slideStatuses.some((slide) => slide.status === "active"));
  const complete = !active && errorCount === 0 && polish.current >= polish.total;
  const steps = slideStatuses.map((slide) =>
    formatPolishProgressStep(slide.position, slide.status, slide.title)
  );

  return {
    id: "polish-slides",
    title: active ? "Polishing slides" : "Polishing complete",
    body:
      errorCount > 0
        ? `${errorCount} slide${errorCount === 1 ? "" : "s"} need a final look.`
        : `Polished ${Math.min(polish.current, polish.total)} of ${polish.total} slide${
            polish.total === 1 ? "" : "s"
          }.`,
    steps,
    stepStatuses: slideStatuses.map((slide) => slide.status),
    currentStepIndex,
    active,
    complete,
  };
}

function getInitialActivityText(progress: CreationProgress): string | null {
  const isTerminal =
    progress.status === "complete" || progress.status === "error" || progress.status === "cancelled";
  if (isTerminal || progress.phase === "polishing") return null;

  const slideCount =
    Number.isFinite(progress.totalSlides) && progress.totalSlides > 0
      ? `${progress.totalSlides}-slide `
      : "";

  return `**Planning the deck build**\n\nI'm starting the ${slideCount}deck build now.`;
}

function hasRenderedApplyEdit(entries: ActivityTimelineEntry[]): boolean {
  return entries.some((entry) =>
    entry.type === "edit_step" ? true : Boolean(entry.attachedEdits?.length)
  );
}

function removeTrailingApplyEditNarration(entries: ActivityTimelineEntry[]): void {
  const previousEntry = entries[entries.length - 1];
  if (previousEntry?.type === "thinking" && isApplyEditNarrationEntry(previousEntry.text)) {
    entries.pop();
  }
}

function getActivityTimelineEntries(progress: CreationProgress): ActivityTimelineEntry[] {
  const entries: ActivityTimelineEntry[] = [];
  const seenCallIds = new Set<string>();
  const hops = progress.agentTimeline?.hops ?? [];
  const includeSlideSummaries =
    progress.streamApplyEditSlideSummaries === true && progress.phase !== "polishing";

  if (hops.length === 0) {
    const initialActivityText = getInitialActivityText(progress);
    if (initialActivityText) {
      appendThinkingEntries(entries, "initial", initialActivityText);
    }
    const polish = getPolishActivity(progress);
    if (polish) {
      entries.push({ id: polish.id, type: "edit_step", edit: polish });
    }
    return entries;
  }

  for (const hop of hops) {
    const hasApplyEditTool = hop.tools.some((tool) => tool.tool === "apply_edits");
    const applyEditSummary = getApplyEditSummary(hop, includeSlideSummaries);
    const beforeThinkingCount = entries.length;
    appendThinkingEntries(entries, `thinking-${hop.hop}`, hop.thinkingText);
    if (hasApplyEditTool || hasRenderedApplyEdit(entries.slice(0, beforeThinkingCount))) {
      for (let index = entries.length - 1; index >= beforeThinkingCount; index -= 1) {
        const entry = entries[index];
        if (entry.type === "thinking" && isApplyEditNarrationEntry(entry.text)) {
          entries.splice(index, 1);
        }
      }
    }

    const outputSummary = outputSummaryToApplyEditActivity(hop, progress, includeSlideSummaries);
    if (outputSummary) {
      removeTrailingApplyEditNarration(entries);
      entries.push({ id: outputSummary.id, type: "edit_step", edit: outputSummary });
      if (outputSummary.active) {
        return entries;
      }
    }

    for (const tool of hop.tools) {
      if (tool.tool !== "apply_edits" || seenCallIds.has(tool.callId)) continue;
      seenCallIds.add(tool.callId);
      const edit = applyEditToolToActivity(tool, applyEditSummary, includeSlideSummaries);
      removeTrailingApplyEditNarration(entries);
      entries.push({ id: edit.id, type: "edit_step", edit });
      if (edit.active) {
        return entries;
      }
    }
  }

  const polish = getPolishActivity(progress);
  if (polish) {
    entries.push({ id: polish.id, type: "edit_step", edit: polish });
  }

  return entries;
}

function hasActiveApplyEdits(entries: ActivityTimelineEntry[]): boolean {
  return entries.some((entry) =>
    entry.type === "edit_step"
      ? entry.edit.active
      : entry.attachedEdits?.some((edit) => edit.active)
  );
}

const ActivityTimelineRow: React.FC<{
  active?: boolean;
  isLast?: boolean;
  children: React.ReactNode;
}> = ({ active = false, isLast = false, children }) => (
  <div className="flex min-w-0 gap-4">
    <div className="flex w-5 shrink-0 flex-col items-center">
      {active ? (
        <Loader2 className="mt-0.5 size-4 animate-spin text-primary" aria-hidden />
      ) : (
        <span className="mt-1.5 size-2.5 rounded-full bg-primary/70" aria-hidden />
      )}
      {!isLast && <span className="mt-2 min-h-12 w-px flex-1 bg-border" aria-hidden />}
    </div>
    <div className={cn("min-w-0 flex-1", isLast ? "pb-0" : "pb-6")}>
      {children}
    </div>
  </div>
);

const EditStepProgress: React.FC<{
  steps: string[];
  stepStatuses?: Array<"pending" | "active" | "done" | "error">;
  active?: boolean;
  currentStepIndex?: number;
  complete?: boolean;
}> = ({ steps, stepStatuses, active = false, currentStepIndex = steps.length - 1, complete = false }) => (
  <div className="mt-2 space-y-1.5">
    {steps.map((step, index) => {
      const status = stepStatuses?.[index];
      const isCurrent = status ? status === "active" : active && index === currentStepIndex;
      const isComplete = status ? status === "done" : complete || (active && index < currentStepIndex);
      return (
        <div
          key={`${index}-${step}`}
          className={cn(
            "flex min-w-0 items-center gap-2 text-[12.5px] leading-[17px]",
            isCurrent && "font-medium",
            isCurrent || isComplete ? "text-foreground" : "text-muted-foreground"
          )}
        >
          <span
            className={cn(
              "flex size-3.5 shrink-0 items-center justify-center rounded-full",
              isCurrent && "bg-primary/10",
              isComplete && "bg-primary/10"
            )}
            aria-hidden
          >
            {isComplete ? (
              <Check className="size-2.5 text-primary" />
            ) : (
              <span
                className={cn(
                  "size-1.5 rounded-full",
                  isCurrent ? "bg-primary" : "bg-muted-foreground/30"
                )}
              />
            )}
          </span>
          <span className="min-w-0">{step}</span>
        </div>
      );
    })}
  </div>
);

const EditActivityView: React.FC<{ edit: EditActivity; nested?: boolean }> = ({ edit, nested = false }) => (
  <div
    className={cn(
      "min-w-0 leading-relaxed",
      nested && "mt-2.5 pl-3"
    )}
  >
    <div className="mb-1.5 text-[12.5px] font-bold leading-[17px] text-foreground">{edit.title}</div>
    {edit.steps?.length ? (
      <>
        {edit.body && !edit.steps.includes(edit.body) ? (
          <div className="mb-2 text-[12.5px] leading-[17px] text-foreground">{edit.body}</div>
        ) : null}
        <EditStepProgress
          steps={edit.steps}
          stepStatuses={edit.stepStatuses}
          active={edit.active}
          currentStepIndex={edit.currentStepIndex}
          complete={edit.complete}
        />
        {edit.footer ? (
          <div className="mt-2 text-[12.5px] leading-[17px] text-foreground">{edit.footer}</div>
        ) : null}
      </>
    ) : (
      <div className="text-[12.5px] leading-[17px] text-foreground">
        {edit.footer ? edit.footer : edit.body}
      </div>
    )}
  </div>
);

const ActivityTimeline: React.FC<{
  entries: ActivityTimelineEntry[];
  isTerminal: boolean;
}> = ({ entries, isTerminal }) => {
  const pulseLatestEntry = !isTerminal && entries.length > 0 && !hasActiveApplyEdits(entries);
  if (entries.length === 0) return null;

  return (
    <div className="relative">
      <div className="relative flex min-w-0 flex-col">
        {entries.map((entry, index) => (
          <ActivityTimelineRow
            key={entry.id}
            isLast={index === entries.length - 1}
            active={
              entry.type === "edit_step"
                ? entry.edit.active || (pulseLatestEntry && index === entries.length - 1)
                : entry.attachedEdits?.some((edit) => edit.active) ||
                  (pulseLatestEntry && index === entries.length - 1)
            }
          >
            {entry.type === "edit_step" ? (
              <EditActivityView edit={entry.edit} />
            ) : (
              <>
                <SimpleMarkdown
                  text={entry.text}
                  compact
                  className={cn("min-w-0 leading-relaxed text-foreground", NARRATOR_MARKDOWN_CLASS)}
                />
                {entry.attachedEdits?.map((edit) => (
                  <EditActivityView key={edit.id} edit={edit} nested />
                ))}
              </>
            )}
          </ActivityTimelineRow>
        ))}
      </div>
    </div>
  );
};

export const DeckCreationProgressView: React.FC<DeckCreationProgressProps> = ({ progress, onCancel }) => {
  const { scrollContainerRef: eventListRef } = useStickToBottom<HTMLDivElement>([
    progress.agentTimeline,
    progress.polish,
    progress.status,
  ]);
  const isComplete = progress.status === "complete";
  const isError = progress.status === "error";
  const isCancelled = progress.status === "cancelled";
  const isTerminal = isComplete || isError || isCancelled;
  const activityEntries = getActivityTimelineEntries(progress);
  const hasActivity = activityEntries.length > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background" style={{ maxWidth: "100%" }}>
      <div className="flex shrink-0 items-start gap-3 border-b border-border/80 bg-background px-4 py-4">
        <div className="min-w-0 flex-1">
          <h3 className="m-0 text-sm font-semibold text-foreground">Agent activity</h3>
          {!isTerminal && (
            <p className="m-0 mt-0.5 text-[11px] leading-snug text-muted-foreground">
              Live build progress
            </p>
          )}
        </div>
        {!isTerminal && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            title="Stop slide generation"
            className="ml-auto flex h-7 shrink-0 items-center gap-1 rounded-md border border-border bg-background px-2.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
          >
            <Square className="h-3 w-3" />
            Stop
          </button>
        )}
      </div>

      <section className="flex min-h-0 flex-1 flex-col bg-background px-4 py-4">
        <div
          ref={eventListRef}
          className="relative min-h-0 flex-1 overflow-y-auto rounded-2xl border border-border bg-background p-4 pr-3"
        >
          {hasActivity ? (
            <ActivityTimeline entries={activityEntries} isTerminal={isTerminal} />
          ) : null}
        </div>
      </section>

      {(isComplete || isCancelled) && (
        <div className="shrink-0 border-t border-border px-4 py-2.5">
          <p className="text-center text-xs text-muted-foreground">
            {isCancelled ? progress.message : "Continue chatting to make edits"}
          </p>
        </div>
      )}
    </div>
  );
};
